<template>
  <div class="debug-banner">
    <div class="debug-controls">
      <div 
        @click="toggleDebugInfo"
        title="Toggle debug info"
        class="toggle-debug-icon"
        :class="{ 'expanded': expanded, 'collapsed': !expanded }"
      >
        <i class="fas fa-bug" />
      </div>
    </div>
    <div
      class="inner-debug-banner"
      v-show="expanded && dbgData"
    >
      <div 
        @click="toggleConsoleLog"
        :title="consoleLogEnabled ? 'Disable console logs' : 'Enable console logs'"
        class="toggle-console-log"
        :class="{ 'enabled': consoleLogEnabled }"
      >
        <i
          class="fas fa-terminal"
          :class="{ 'enabled': consoleLogEnabled }"
        />
      </div>    
      <div
        @click="toggleUIDebug"
        :title="uiDebugEnabled ? 'Disable UI debug' : 'Enable UI debug'"
        class="toggle-ui-debug"
        :class="{ 'enabled': uiDebugEnabled }"
      >
        <i class="fas fa-eye" />
      </div>
      <div
        @click="toggleUsingFakeData"
        :title="usingFakeData ? 'Disable using fake data' : 'Enable using fake data'"
        class="toggle-using-fake-data"
        :class="{ 'enabled': usingFakeData }"
      >
        <i class="fas fa-database" />
      </div>
      [{{ i }}] r7eApp: {{ dbgData.r7eApp }}
      <div
        v-show="buildInfo"
      >
        {{ buildInfo }}
      </div>
      <h4
        @click="expandedAuth = !expandedAuth"
        style="cursor: pointer;"
      >
        Auth
      </h4>
      <div v-show="expandedAuth">
        <strong>Id:</strong> {{ dbgData?.data?.userid }}<br>
        <strong>Username:</strong> {{ dbgData?.data?.username }}<br>
        <strong>DisplayName:</strong> {{ dbgData?.data?.name }}<br>
        <strong>isAuthenticated:</strong> {{ dbgData.isAuthenticated }}<br>
        <strong>hasAccessToCurrentService:</strong> {{ dbgData.hasAccessToCurrentService }}<br>
        <strong>hasPendingRegistration:</strong> {{ dbgData.hasPendingRegistration }}<br>
        <hr>
        <strong>authData:</strong> {{ dbgData?.data }}        
      </div>
      <h4
        @click="expandedEnvVariables = !expandedEnvVariables"
        style="cursor: pointer;"
      >
        Environment Variables
      </h4>
      <div
        v-for="(value, key) in envVariables"
        :key="key"
        v-show="expandedEnvVariables"
      >
        <strong>{{ key }}:</strong> {{ value }}
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, defineProps, onUnmounted, watch } from 'vue';

const props = defineProps({
  r7eApp: {
    type: Object,
    required: true
  },
  buildInfo: {
    type: Object,
    required: true
  }
});

const dbgData = ref({ });
const envVariables = ref(process.env);
const i = ref(1);
const expanded = ref(false);
const expandedEnvVariables = ref(
  localStorage.getItem('debugInfo.expandedEnvVariables') === 'true'
);
const expandedAuth = ref(
  localStorage.getItem('debugInfo.expandedAuth') === 'true'
);
const consoleLogEnabled = ref(false);
const uiDebugEnabled = ref(false);
const usingFakeData = ref(false);
// Watch and save to localStorage
watch(expandedEnvVariables, (newValue) => {
  localStorage.setItem('debugInfo.expandedEnvVariables', String(newValue));
});

watch(expandedAuth, (newValue) => {
  localStorage.setItem('debugInfo.expandedAuth', String(newValue));
});

watch(usingFakeData, (newValue) => {
  localStorage.setItem('debugInfo.usingFakeData', String(newValue));
});

const toggleDebugInfo = () => {
  expanded.value = !expanded.value;
  if (props.r7eApp) {
    props.r7eApp.setDebugModeVisible(expanded.value);
  }
};

const toggleConsoleLog = () => {
  consoleLogEnabled.value = !consoleLogEnabled.value;
  if (props.r7eApp) {
    props.r7eApp.setConsoleLogEnabled(consoleLogEnabled.value);
  }
};
const toggleUIDebug = () => {
  uiDebugEnabled.value = !uiDebugEnabled.value;
  if (props.r7eApp) {
    props.r7eApp.setUIDebugEnabled(uiDebugEnabled.value);
  }
};
const toggleUsingFakeData = () => {
  usingFakeData.value = !usingFakeData.value;
  if (props.r7eApp) {
    props.r7eApp.setUsingFakeData(usingFakeData.value);
  }
};
const debugManagerCallback = (o) => {
  expanded.value = o.visible;
}

const debugCredentialsCallback = (o) => {
  i.value += 1;
  dbgData.value.data = o.data;
  dbgData.value.r7eApp = props.r7eApp.constructor.name;
  dbgData.value.username = o.username;
  dbgData.value.name = o.name;
  dbgData.value.isAuthenticated = props.r7eApp.isAuthenticated();
  dbgData.value.hasAccessToCurrentService = props.r7eApp.hasAccessToCurrentService();
  dbgData.value.hasPendingRegistration = props.r7eApp.hasPendingRegistrationForCurrentService();
}

onMounted(() => {
  const debugManager = props.r7eApp.getDebugManager();
  if (debugManager) {
    debugManager.registerObserver(debugManagerCallback);
    expanded.value = debugManager.isVisible();
  } else {
    expanded.value = false;
  }

  props.r7eApp.registerCredentialsObserver(debugCredentialsCallback);

  expanded.value = props.r7eApp.getDebugManager().isVisible();

  // Initialize console log enabled state
  if (props.r7eApp) {
    consoleLogEnabled.value = props.r7eApp.isConsoleLogEnabled();
    uiDebugEnabled.value = props.r7eApp.isUIDebugEnabled();
    usingFakeData.value = props.r7eApp.isUsingFakeData();
  }
});

onUnmounted(() => {
  // console.log('DebugInfo, onUnmounted');
  props.r7eApp.getDebugManager().unregisterObserver(debugManagerCallback);
  props.r7eApp.unregisterCredentialsObserver(debugCredentialsCallback);
});

</script>

<style scoped>
.debug-banner {
  position: fixed;
  bottom: 0;
  right: 0;
  left: var(--sidebar-width, 0);
  font-size: .8rem;
  /* border: 1px solid red; */
}
.toggle-debug-icon {
  position: absolute;
  bottom: 0;
  right: 0.3rem;
  cursor: pointer;
  font-size: 1rem;
}
.toggle-debug-icon.expanded {
  z-index: 1000;
}

.inner-debug-banner {
  font-size: .8rem;
  padding: 1rem;
  background-color: var(--color-debug-bg);
  color: var(--color-debug-text);
  opacity: 0.5;
}
.inner-debug-banner strong {
  opacity: .5;
}
.debug-banner hr {
  border: 1px solid var(--color-debug-text);
}
.debug-banner h4 {
  cursor: pointer;
  border: 1px solid var(--color-debug-text);
  padding: 0.2rem;
  margin-bottom: .2rem;
  background-color: var(--color-debug-text);
  color: var(--color-debug-bg);
  opacity: 0.5;
}
.debug-controls {
  position: absolute;
  bottom: .1rem;
  right: .1rem;
  display: flex;
  gap: 0.5rem;
  align-items: center;
  z-index: 1000;
}

.toggle-using-fake-data,
.toggle-ui-debug,
.toggle-console-log {
  position: absolute;
  bottom: 0;
  right: 2rem;
  cursor: pointer;
  font-size: 0.8rem;
  padding-bottom: 0.1rem;
  color: var(--color-debug-text);
  opacity: 0.3;
}
.toggle-ui-debug {
  right: 3rem;
}
.toggle-using-fake-data {
  right: 4rem;
}

.toggle-ui-debug.enabled,
.toggle-console-log.enabled,
.toggle-using-fake-data.enabled {
  opacity: 1;
}
</style>